import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import VolumeSection from '../components/VolumeSection';
import CallsBadge from '../components/CallsBadge';
import ImportantNotesButton from '../components/ImportantNotesButton';
import ImportantNotesBanner from '../components/ImportantNotesBanner';
import { useAuth, useCan } from '../contexts/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import usePresence from '../hooks/usePresence';
import PresenceBadge from '../components/PresenceBadge';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ArrowLeft, Pencil, Phone, Mail, User, MapPin,
    Calendar as CalendarIcon, Building, FileText, Target,
    Layers, Truck, Check, Clock, Send, ClipboardList, Tag, Package, DollarSign,
    Calculator, Gift, Paperclip, X, Lock
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUTS = [
    { value: 'nouveau', label: 'Nouveau', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    { value: 'sans_reponse_1', label: 'Sans réponse 1', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    { value: 'sans_reponse_2', label: 'Sans réponse 2', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    { value: 'sans_reponse_3', label: 'Sans réponse 3', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    { value: 'relance_whatsapp', label: 'Sans réponse WhatsApp', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    { value: 'a_rappeler', label: 'À rappeler', color: 'bg-purple-100 text-purple-700 border-purple-200' },
    { value: 'visite_virtuelle', label: 'Visite virtuelle', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    { value: 'visite_presentiel', label: 'Visite présentiel', color: 'bg-green-100 text-green-700 border-green-200' },
    { value: 'devis_estimatif', label: 'Devis estimatif', color: 'bg-teal-100 text-teal-700 border-teal-200' },
    { value: 'listing_attente', label: 'Listing en attente', color: 'bg-gray-100 text-gray-700 border-gray-200' },
    { value: 'listing_telephone', label: 'Listing téléphone', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    { value: 'listing_email', label: 'Listing email', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    { value: 'faux_contact', label: 'Faux contact', color: 'bg-red-100 text-red-700 border-red-200' },
    { value: 'non_interessant', label: 'Non intéressant', color: 'bg-gray-100 text-gray-600 border-gray-200' },
];

const VISIT_STATUSES = ['visite_presentiel', 'visite_virtuelle'];

const FORMULE_LABELS = {
    economique: 'Economique',
    economique_plus: 'Economique plus',
    standard: 'Standard',
    luxe: 'Luxe',
};

const FORMULE_COLUMNS = [
    { key: 'economique', label: 'ECONOMIQUE', color: 'text-green-600', border: 'border-l-green-500' },
    { key: 'economique_plus', label: 'ECO. PLUS', color: 'text-amber-600', border: 'border-l-amber-500' },
    { key: 'standard', label: 'STANDARD', color: 'text-blue-600', border: 'border-l-blue-500' },
    { key: 'luxe', label: 'LUXE', color: 'text-red-500', border: 'border-l-red-400' },
];

const GARDE_MEUBLE_LABELS = {
    non: 'Non',
    oui: 'Oui',
    a_definir: 'A definir',
};

const SOURCES = [
    { value: 'appel_simple', label: 'Appel simple' },
    { value: 'appel_google', label: 'Appel Google' },
    { value: 'formulaire_site', label: 'Formulaire site' },
    { value: 'message_google', label: 'Message Google' },
    { value: 'recommandation', label: 'Recommandation' },
    { value: 'ancien_client', label: 'Ancien client' },
    { value: 'plateforme_externe', label: 'Plateforme externe' },
];

const DEPARTEMENTS = [
    { value: '75', label: '75 - Paris' },
    { value: '77', label: '77 - Seine-et-Marne' },
    { value: '78', label: '78 - Yvelines' },
    { value: '91', label: '91 - Essonne' },
    { value: '92', label: '92 - Hauts-de-Seine' },
    { value: '93', label: '93 - Seine-Saint-Denis' },
    { value: '94', label: '94 - Val-de-Marne' },
    { value: '95', label: "95 - Val-d'Oise" },
    { value: 'hors_idf', label: 'Hors Île-de-France' },
];

const ASCENSEURS = [
    { value: 'non', label: 'Non' },
    { value: '1_3', label: '1 à 3 personnes' },
    { value: '4_6', label: '4 à 6 personnes' },
    { value: '7_9', label: '7 à 9 personnes' },
    { value: '10_plus', label: '10 personnes et plus' },
    { value: 'indetermine', label: 'Indéterminé' },
    { value: 'interdit', label: 'Interdit' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
    const h = i.toString().padStart(2, '0');
    return [
        { value: `${h}:00`, label: `${h}:00` },
        { value: `${h}:30`, label: `${h}:30` },
    ];
}).flat();

// Add `addMin` minutes to an `HH:MM` string. Wraps to 23:30 if it would overflow past midnight.
function addMinutes(time, addMin) {
    if (!time || !time.includes(':')) return time;
    const [hh, mm] = time.split(':').map(Number);
    const total = hh * 60 + mm + addMin;
    if (total >= 24 * 60) return '23:30';
    const nh = Math.floor(total / 60).toString().padStart(2, '0');
    const nm = (total % 60).toString().padStart(2, '0');
    return `${nh}:${nm}`;
}

// Today's date (YYYY-MM-DD) for the Date input min attribute
function todayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const CAN_ASSIGN = ['admin', 'assistante', 'responsable'];

function InfoCard({ title, icon: Icon, children, completed }) {
    return (
        <Card className="overflow-hidden">
            <div className="flex items-center gap-3 p-4 border-b bg-gray-50">
                <div className={`w-1 h-8 rounded-full ${completed ? 'bg-green-500' : 'bg-[#C9A962]'}`} />
                <Icon className={`w-5 h-5 ${completed ? 'text-green-600' : 'text-[#1B365D]'}`} />
                <h3 className="text-lg font-semibold text-[#1B365D]">{title}</h3>
                {completed && <Check className="w-5 h-5 text-green-500 ml-auto" />}
            </div>
            <CardContent className="p-0">{children}</CardContent>
        </Card>
    );
}

function InfoRow({ label, value, icon: Icon }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center py-4 px-6 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center gap-3 sm:w-1/3 mb-1 sm:mb-0">
                {Icon && <Icon className="w-4 h-4 text-gray-400" />}
                <span className="text-gray-500 text-sm">{label}</span>
            </div>
            <div className="sm:w-2/3">
                <span className="text-[#1B365D] font-medium">{value || '-'}</span>
            </div>
        </div>
    );
}

export default function ProspectDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isExtended = searchParams.get('context') === 'extended';
    const fromPage = searchParams.get('from');
    const fromEventId = searchParams.get('event_id');
    const backUrl = fromEventId ? `/agenda/${fromEventId}` : (fromPage === 'agenda' ? '/agenda' : fromPage === 'inventaire' ? '/inventaire' : fromPage === 'devis' ? '/devis' : '/prospects');
    const { user } = useAuth();
    const { canWrite: canWriteProspect } = useCan('prospects');
    const [prospect, setProspect] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updatingStatus, setUpdatingStatus] = useState(false);

    // Live presence: who else is currently on this prospect detail page
    const presenceOthers = usePresence('prospect', id);

    // Visit scheduling modal
    const [showVisitModal, setShowVisitModal] = useState(false);
    const [visitType, setVisitType] = useState('visite_presentiel');
    const [visitForm, setVisitForm] = useState({ date: '', start_time: '09:00', end_time: '10:00', assigned_to: '' });
    // When set, the modal updates an existing event instead of creating a new one.
    const [reprogramVisitId, setReprogramVisitId] = useState(null);
    const [users, setUsers] = useState([]);
    const [creatingVisit, setCreatingVisit] = useState(false);
    const [emailConfirm, setEmailConfirm] = useState(null);
    const [sendingEmail, setSendingEmail] = useState(false);
    const canAssign = CAN_ASSIGN.includes(user?.role);
    const { setHasChanges, UnsavedDialog, confirmNavigation } = useUnsavedChanges();

    // Inventaire data
    const [inventaire, setInventaire] = useState(null);
    const [editingInv, setEditingInv] = useState(false);
    const [savingInv, setSavingInv] = useState(false);
    const [invForm, setInvForm] = useState({});
    // Agenda summary per date (camions+effectif déjà programmés)
    const [agendaSummary, setAgendaSummary] = useState({});
    // Sans-reponse chain availability + remark dialog
    const [statutAvailability, setStatutAvailability] = useState({});
    const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState('');
    const [remarkText, setRemarkText] = useState('');

    // Rappel dialog (statuts a_rappeler / listing_attente)
    const [rappelDialogOpen, setRappelDialogOpen] = useState(false);
    const [pendingRappelStatus, setPendingRappelStatus] = useState('');
    const [rappelRemark, setRappelRemark] = useState('');
    const [rappelDate, setRappelDate] = useState('');
    const [savingRappel, setSavingRappel] = useState(false);
    const [savingRemark, setSavingRemark] = useState(false);

    useEffect(() => { fetchProspect(); }, [id]);
    useEffect(() => {
        axios.get(`${API_URL}/api/users/list`, { withCredentials: true })
            .then(r => setUsers(r.data)).catch(() => {});
    }, []);

    useEffect(() => { fetchInventaire(); }, [id]);

    // TZ-safe ISO -> YYYY-MM-DD using LOCAL date parts
    const isoToLocalDate = (iso) => {
        if (!iso) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    // Fetch agenda aggregated camions/effectif for prospect's dates
    useEffect(() => {
        if (!prospect) return;
        const dates = new Set();
        const depDate = isoToLocalDate(prospect.adresse_depart?.date);
        const arrDate = isoToLocalDate(prospect.adresse_arrivee?.date);
        if (depDate) dates.add(depDate);
        if (arrDate) dates.add(arrDate);
        (prospect.prepar_stock || []).forEach(ps => { if (ps.date) dates.add(ps.date); });
        if (dates.size === 0) { setAgendaSummary({}); return; }
        let cancelled = false;
        axios.get(`${API_URL}/api/agenda-demenagement/summary`, {
            params: { dates: Array.from(dates).join(',') },
            withCredentials: true,
        }).then(r => { if (!cancelled) setAgendaSummary(r.data || {}); })
          .catch(() => { if (!cancelled) setAgendaSummary({}); });
        return () => { cancelled = true; };
    }, [prospect?.adresse_depart?.date, prospect?.adresse_arrivee?.date, JSON.stringify((prospect?.prepar_stock || []).map(ps => ps.date))]);

    const fetchInventaire = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/inventaire-prospects?prospect_id=${id}`, { withCredentials: true });
            if (res.data && res.data.length > 0) {
                const inv = res.data[0];
                setInventaire(inv);
                setInvForm({
                    commercial_id: inv.commercial_id || '',
                    commercial_name: inv.commercial_name || '',
                    prospect_statut: inv.prospect_statut || '',
                });
            }
        } catch { /* no inventaire */ }
    };

    const handleSaveInv = async () => {
        if (!inventaire) return;
        setSavingInv(true);
        try {
            await axios.put(`${API_URL}/api/inventaire-prospects/${inventaire.id}`, invForm, { withCredentials: true });
            toast.success('Inventaire mis a jour');
            setEditingInv(false);
            await fetchInventaire();
        } catch { toast.error('Erreur sauvegarde inventaire'); }
        finally { setSavingInv(false); }
    };

    const handleInvCommercialChange = (userId) => {
        const u = users.find(x => x.id === userId);
        setInvForm(prev => ({ ...prev, commercial_id: userId, commercial_name: u ? u.name : '' }));
    };

    const fetchProspect = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/prospects/${id}`, { withCredentials: true });
            setProspect(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement du prospect');
            navigate(backUrl);
        } finally {
            setLoading(false);
        }
    };

    const SANS_REPONSE_REMARK = ['sans_reponse_1', 'sans_reponse_2', 'sans_reponse_3', 'relance_whatsapp', 'faux_contact', 'non_interessant'];
    // Statuts requiring a remark via dialog AND subject to chronological gating
    // (each step needs the previous to be done at least 1 day earlier).
    // faux_contact / non_interessant are NOT in this chain — they only need a
    // remark, the user can switch to them at any time.
    const SANS_REPONSE_CHAIN = ['sans_reponse_1', 'sans_reponse_2', 'sans_reponse_3', 'relance_whatsapp'];
    const RAPPEL_STATUTS = ['a_rappeler', 'listing_attente'];

    const fetchStatutAvailability = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/prospects/${id}/statut-availability`, { withCredentials: true });
            setStatutAvailability(res.data || {});
        } catch { /* silent */ }
    };

    useEffect(() => { if (id) fetchStatutAvailability(); }, [id, prospect?.statut]);

    const isStatutLocked = (value) => {
        if (!SANS_REPONSE_CHAIN.includes(value)) return false;
        if (value === 'sans_reponse_1') {
            // Locked only if we cannot go back to nouveau and we're already past
            return false;
        }
        const info = statutAvailability[value];
        if (!info || !info.available_from) return true; // previous step not done yet
        return new Date() < new Date(info.available_from);
    };

    const statutLockLabel = (value) => {
        const info = statutAvailability[value];
        if (!info) return '';
        if (!info.prev_reached_at) return `Requiert « ${info.requires_prev || ''} »`;
        if (info.available_from) {
            const d = new Date(info.available_from);
            return `Disponible dès le ${d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}`;
        }
        return '';
    };

    const handleStatusChange = async (newStatus) => {
        // Block going back to "nouveau" once left
        if (newStatus === 'nouveau' && prospect.statut !== 'nouveau') {
            toast.error('Impossible de revenir au statut « Nouveau ».');
            return;
        }
        // Intercept any sans-reponse status → require a remark first
        if (SANS_REPONSE_REMARK.includes(newStatus)) {
            setPendingStatus(newStatus);
            setRemarkText('');
            setRemarkDialogOpen(true);
            return;
        }
        // Intercept rappel statuses → require remark + date
        if (RAPPEL_STATUTS.includes(newStatus)) {
            setPendingRappelStatus(newStatus);
            setRappelRemark('');
            setRappelDate('');
            setRappelDialogOpen(true);
            return;
        }
        // If status is a visit type, open scheduling modal (or reschedule mode
        // if the prospect already has an active visit).
        if (VISIT_STATUSES.includes(newStatus)) {
            setVisitType(newStatus);
            try {
                const r = await axios.get(`${API_URL}/api/prospects/${id}/active-visit`, { withCredentials: true });
                const av = r.data?.active_visit;
                if (av) {
                    setReprogramVisitId(av.id);
                    setVisitForm({
                        date: av.date || '',
                        start_time: av.start_time || '09:00',
                        end_time: av.end_time || '10:00',
                        assigned_to: av.assigned_to || '',
                    });
                    // If the active visit is of a different type, the modal will
                    // also update the type to the new one (visite_presentiel <-> visite_virtuelle).
                    setShowVisitModal(true);
                    setHasChanges(true);
                    return;
                }
            } catch (_) { /* fall through to create flow */ }
            setReprogramVisitId(null);
            setVisitForm({ date: '', start_time: '09:00', end_time: '10:00', assigned_to: '' });
            setShowVisitModal(true);
            setHasChanges(true);
            return;
        }
        // Otherwise, just update status
        setUpdatingStatus(true);
        try {
            await axios.patch(`${API_URL}/api/prospects/${id}/statut`, { statut: newStatus, expected_updated_at: prospect?.updated_at }, { withCredentials: true });
            setProspect(prev => ({ ...prev, statut: newStatus }));
            toast.success('Statut mis à jour');
            fetchStatutAvailability();
        } catch (error) {
            if (error?.response?.status === 409) {
                const d = error.response.data?.detail || {};
                toast.error(`Conflit — ${d.modified_by_name || 'un autre utilisateur'} a modifié cette fiche. Rechargement…`);
                fetchProspect();
            } else {
                toast.error(error?.response?.data?.detail || 'Erreur lors de la mise à jour du statut');
            }
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleRemarkConfirm = async () => {
        if (!remarkText.trim()) {
            toast.error('Veuillez saisir une remarque');
            return;
        }
        setSavingRemark(true);
        try {
            await axios.patch(`${API_URL}/api/prospects/${id}/statut`, {
                statut: pendingStatus,
                remarque: remarkText.trim(),
                expected_updated_at: prospect?.updated_at,
            }, { withCredentials: true });
            setProspect(prev => ({ ...prev, statut: pendingStatus }));
            toast.success('Statut mis à jour');
            setRemarkDialogOpen(false);
            setRemarkText('');
            setPendingStatus('');
            fetchStatutAvailability();
        } catch (error) {
            if (error?.response?.status === 409) {
                const d = error.response.data?.detail || {};
                toast.error(`Conflit — ${d.modified_by_name || 'un autre utilisateur'} a modifié cette fiche. Rechargement…`);
                fetchProspect();
            } else {
                toast.error(error?.response?.data?.detail || 'Erreur');
            }
        } finally {
            setSavingRemark(false);
        }
    };

    const handleRappelConfirm = async () => {
        if (!rappelRemark.trim()) {
            toast.error('Veuillez saisir une remarque');
            return;
        }
        if (!rappelDate) {
            toast.error('Veuillez sélectionner une date de rappel');
            return;
        }
        setSavingRappel(true);
        try {
            await axios.patch(`${API_URL}/api/prospects/${id}/statut`, {
                statut: pendingRappelStatus,
                remarque: rappelRemark.trim(),
                next_rappel_date: rappelDate,
                expected_updated_at: prospect?.updated_at,
            }, { withCredentials: true });
            setProspect(prev => ({ ...prev, statut: pendingRappelStatus, next_rappel_date: rappelDate, last_rappel_remark: rappelRemark.trim() }));
            toast.success('Rappel programmé');
            setRappelDialogOpen(false);
            setRappelRemark('');
            setRappelDate('');
            setPendingRappelStatus('');
            fetchStatutAvailability();
        } catch (error) {
            if (error?.response?.status === 409) {
                const d = error.response.data?.detail || {};
                toast.error(`Conflit — ${d.modified_by_name || 'un autre utilisateur'} a modifié cette fiche. Rechargement…`);
                fetchProspect();
            } else {
                toast.error(error?.response?.data?.detail || 'Erreur');
            }
        } finally {
            setSavingRappel(false);
        }
    };

    const handleCreateVisit = async (e) => {
        e.preventDefault();
        if (!visitForm.date) { toast.error('La date est requise'); return; }
        setCreatingVisit(true);
        try {
            const typeLabel = visitType === 'visite_presentiel' ? 'Visite présentiel' : 'Visite virtuelle';
            const addr = prospect.adresse_depart?.adresse || '';
            const payload = {
                title: `${typeLabel} - ${prospect.nom}`,
                type: visitType,
                date: visitForm.date,
                start_time: visitForm.start_time,
                end_time: visitForm.end_time,
                location: addr,
                description: `Prospect: ${prospect.nom}${prospect.telephone ? ` | Tél: ${prospect.telephone}` : ''}${prospect.email ? ` | Email: ${prospect.email}` : ''}`,
                prospect_id: id,
                assigned_to: visitForm.assigned_to || undefined,
            };
            let eventId;
            if (reprogramVisitId) {
                // Reschedule the existing active visit (PUT). Force visit_status
                // back to 'planifiee' in case it was 'reportee'.
                await axios.put(`${API_URL}/api/events/${reprogramVisitId}`, {
                    ...payload,
                    visit_status: 'planifiee',
                }, { withCredentials: true });
                eventId = reprogramVisitId;
            } else {
                const res = await axios.post(`${API_URL}/api/events`, payload, { withCredentials: true });
                eventId = res.data?.id;
            }
            // Also update prospect status
            await axios.patch(`${API_URL}/api/prospects/${id}/statut`, { statut: visitType }, { withCredentials: true });
            setProspect(prev => ({ ...prev, statut: visitType }));
            setShowVisitModal(false);
            setReprogramVisitId(null);
            setHasChanges(false);
            toast.success(reprogramVisitId
                ? `${typeLabel} reprogrammée avec succès`
                : `${typeLabel} planifiée et ajoutée à l'agenda`);

            // If prospect has email, show email confirmation dialog
            if (prospect.email) {
                setTimeout(() => {
                    setEmailConfirm({
                        eventId: eventId,
                        clientName: prospect.nom,
                        clientEmail: prospect.email,
                        date: visitForm.date,
                        heure: visitForm.start_time,
                        visitType: visitType,
                        address: addr,
                        prospectId: id,
                    });
                }, 500);
            }
        } catch (error) {
            const detail = error?.response?.data?.detail;
            if (typeof detail === 'object' && detail?.code === 'active_visit_exists') {
                toast.error(detail.message || 'Visite déjà existante');
            } else {
                toast.error('Erreur lors de la planification');
            }
        } finally {
            setCreatingVisit(false);
        }
    };

    const handleSendConfirmationEmail = async () => {
        if (!emailConfirm) return;
        setSendingEmail(true);
        try {
            await axios.post(`${API_URL}/api/emails/send-rdv-confirmation`, {
                to_email: emailConfirm.clientEmail,
                client_name: emailConfirm.clientName,
                date: emailConfirm.date,
                heure: emailConfirm.heure,
                visit_type: emailConfirm.visitType,
                address: emailConfirm.address,
                event_id: emailConfirm.eventId,
                prospect_id: emailConfirm.prospectId,
            }, { withCredentials: true });
            toast.success('Le mail est bien envoyé merci 😎');
            setEmailConfirm(null);
        } catch (err) {
            const msg = err.response?.data?.detail || "Erreur lors de l'envoi de l'email";
            toast.error(msg);
        } finally {
            setSendingEmail(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try { return format(new Date(dateStr), 'dd MMMM yyyy', { locale: fr }); }
        catch { return dateStr; }
    };
    const getSourceLabel = (s) => SOURCES.find(x => x.value === s)?.label || s || '-';
    const getDepartementLabel = (d) => DEPARTEMENTS.find(x => x.value === d)?.label || d || '-';
    const getAscenseurLabel = (a) => ASCENSEURS.find(x => x.value === a)?.label || a || '-';
    const getStatusConfig = (statut) => STATUTS.find(s => s.value === statut) || STATUTS[0];

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center h-64">
                    <div className="w-8 h-8 border-4 border-[#1B365D] border-t-transparent rounded-full animate-spin" />
                </div>
            </Layout>
        );
    }
    if (!prospect) return null;

    const statusConfig = getStatusConfig(prospect.statut);

    return (
        <Layout>
            <Toaster position="top-right" richColors />
            <div className="max-w-6xl mx-auto animate-fadeIn">
                {/* Header (sticky) */}
                <div className="sticky top-0 z-40 -mx-4 px-4 py-4 mb-6 bg-white/90 backdrop-blur-md border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={() => confirmNavigation(() => navigate(backUrl))} className="text-[#1B365D] hover:bg-[#1B365D]/10" data-testid="back-button">
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>{prospect.nom}</h1>
                            <p className="text-gray-500 text-sm">Créé le {formatDate(prospect.created_at)}</p>
                        </div>
                        <CallsBadge item={{ id: id, prospect_id: id, nom: prospect.nom, telephone: prospect.telephone, calls_count: prospect.calls_count, calls_incoming: prospect.calls_incoming, calls_outgoing: prospect.calls_outgoing }} />
                        <PresenceBadge others={presenceOthers} />
                    </div>
                    <div className="flex items-center gap-2">
                        <ImportantNotesButton
                            prospectId={id}
                            prospectName={prospect.nom}
                            variant="header"
                        />
                        {isExtended && inventaire && (
                            <Button variant="outline" onClick={async () => {
                                try {
                                    toast.loading('Generation du PDF...', { id: 'inv-pdf' });
                                    const res = await axios.get(`${API_URL}/api/prospects/${id}/inventaire-pdf`, {
                                        responseType: 'blob', withCredentials: true,
                                    });
                                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `Inventaire_${(prospect.nom || 'prospect').replace(/\s+/g, '_')}.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    setTimeout(() => URL.revokeObjectURL(url), 500);
                                    toast.success('PDF telecharge', { id: 'inv-pdf' });
                                } catch (e) {
                                    console.error(e);
                                    toast.error('Erreur generation PDF', { id: 'inv-pdf' });
                                }
                            }} className="gap-2 border-gray-300" data-testid="generate-pdf-btn">
                                <FileText className="w-4 h-4" />
                                Generer PDF
                            </Button>
                        )}
                        <Button onClick={() => navigate(`/prospects/${id}/modifier${isExtended ? `?context=extended${fromPage ? `&from=${fromPage}` : ''}${fromEventId ? `&event_id=${fromEventId}` : ''}` : ''}`)}
                            disabled={!canWriteProspect}
                            title={!canWriteProspect ? 'Accès refusé : permission Écriture requise' : 'Modifier'}
                            className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="edit-button">
                            <Pencil className="w-4 h-4" />
                            Modifier
                        </Button>
                    </div>
                </div>

                {/* Important Notes Banner */}
                <ImportantNotesBanner prospectId={id} />

                {/* Status Card */}
                <Card className="mb-6 border-2 border-[#C9A962]/30 bg-gradient-to-r from-[#1B365D]/5 to-[#C9A962]/10">
                    <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Layers className="w-6 h-6 text-[#C9A962]" />
                                <span className="text-lg font-semibold text-[#1B365D]">Statut du prospect</span>
                            </div>
                            <Select value={prospect.statut} onValueChange={handleStatusChange} disabled={updatingStatus}>
                                <SelectTrigger className={`w-full sm:w-64 ${statusConfig.color} border font-medium`} data-testid="status-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUTS.map((s) => {
                                        const locked = isStatutLocked(s.value) || (s.value === 'nouveau' && prospect.statut !== 'nouveau');
                                        return (
                                            <SelectItem
                                                key={s.value}
                                                value={s.value}
                                                disabled={locked}
                                                data-testid={`statut-item-${s.value}`}
                                            >
                                                <span className="flex items-center justify-between gap-2 w-full">
                                                    <span>{s.label}</span>
                                                    {locked && (
                                                        <span className="text-[10px] text-gray-400 italic ml-2">
                                                            {s.value === 'nouveau' ? 'verrouillé' : statutLockLabel(s.value) || 'verrouillé'}
                                                        </span>
                                                    )}
                                                </span>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Inventaire Sections - only from Agenda/Inventaire */}
                {isExtended && inventaire && (
                    <div className="space-y-4 mb-6">
                        {/* Statut Inventaire - Yellow border */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-amber-400 p-5" data-testid="section-statut-inventaire">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Statut inventaire</p>
                                    <p className="text-lg font-bold text-gray-900">
                                        {inventaire.statut_inventaire === 'nouveau' ? 'Nouveau' : inventaire.statut_inventaire === 'en_redaction' ? 'En redaction' : 'Termine'}
                                    </p>
                                </div>
                                <Badge className={`text-sm px-3 py-1 ${
                                    inventaire.statut_inventaire === 'termine' ? 'bg-emerald-100 text-emerald-700' :
                                    inventaire.statut_inventaire === 'en_redaction' ? 'bg-amber-100 text-amber-700' :
                                    'bg-blue-100 text-blue-700'
                                }`} data-testid="inventaire-statut-badge">
                                    {inventaire.statut_inventaire === 'nouveau' ? 'Nouveau' : inventaire.statut_inventaire === 'en_redaction' ? 'En redaction' : 'Termine'}
                                </Badge>
                            </div>
                        </div>

                        {/* Commercial et Type - Red border */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-red-400 p-5" data-testid="section-commercial-type">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-base font-bold text-gray-900">Commercial et Type</h3>
                                {!editingInv ? (
                                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-[#E67E22] border-[#E67E22]/30 hover:bg-[#E67E22]/5"
                                        onClick={() => setEditingInv(true)} data-testid="edit-inv-btn">
                                        <Pencil className="w-3 h-3" /> Modifier
                                    </Button>
                                ) : (
                                    <div className="flex gap-1.5">
                                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setEditingInv(false); setInvForm({ commercial_id: inventaire.commercial_id || '', commercial_name: inventaire.commercial_name || '', prospect_statut: inventaire.prospect_statut || '' }); }} data-testid="cancel-inv-btn">
                                            Annuler
                                        </Button>
                                        <Button size="sm" className="h-7 text-xs bg-[#1B365D] hover:bg-[#152a4a] text-white" onClick={handleSaveInv} disabled={savingInv} data-testid="save-inv-btn">
                                            {savingInv ? 'Sauvegarde...' : 'Enregistrer'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <div className="space-y-0 divide-y divide-gray-100">
                                <div className="flex items-center justify-between py-3">
                                    <span className="text-sm text-gray-500 flex items-center gap-2">
                                        <Building className="w-4 h-4" /> Commercial
                                    </span>
                                    {editingInv && user?.role === 'admin' ? (
                                        <Select value={invForm.commercial_id || '_none'} onValueChange={handleInvCommercialChange}>
                                            <SelectTrigger className="w-48 h-8 text-sm" data-testid="inv-commercial-select">
                                                <SelectValue placeholder="Selectionner" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {users.map(u => (
                                                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <span className="text-sm font-medium text-gray-900 flex items-center gap-2" data-testid="inv-commercial-value">
                                            {inventaire.commercial_name || '-'}
                                            <Lock className="w-3 h-3 text-gray-400" title="Commercial verrouillé (modifiable par un admin uniquement)" />
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center justify-between py-3">
                                    <span className="text-sm text-gray-500 flex items-center gap-2">
                                        <Tag className="w-4 h-4" /> Type
                                    </span>
                                    {editingInv ? (
                                        <Select value={invForm.prospect_statut || '_none'} onValueChange={(val) => setInvForm(prev => ({ ...prev, prospect_statut: val }))}>
                                            <SelectTrigger className="w-48 h-8 text-sm" data-testid="inv-type-select">
                                                <SelectValue placeholder="Selectionner" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {STATUTS.map(s => (
                                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <span className="text-sm font-medium text-gray-900" data-testid="inv-type-value">
                                            {STATUTS.find(s => s.value === inventaire.prospect_statut)?.label || '-'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Info Sections */}
                <div className="space-y-4">
                    <InfoCard title="Source" icon={Target} completed={!!(prospect.source && prospect.departement)}>
                        <InfoRow label="Source du prospect" value={getSourceLabel(prospect.source)} icon={Target} />
                        <InfoRow label="Département" value={getDepartementLabel(prospect.departement)} icon={Building} />
                    </InfoCard>
                    <InfoCard title="Contact" icon={User} completed={!!(prospect.nom && (prospect.telephone || prospect.email))}>
                        <InfoRow label="Nom" value={prospect.nom} icon={User} />
                        <InfoRow label="Téléphone" value={prospect.telephone} icon={Phone} />
                        <InfoRow label="Email" value={prospect.email} icon={Mail} />
                    </InfoCard>
                    <InfoCard title="Adresse de départ" icon={Truck} completed={!!(prospect.adresse_depart?.adresse)}>
                        {prospect.adresse_depart ? (
                            <>
                                <InfoRow label="Date chargement" value={formatDate(prospect.adresse_depart.date)} icon={CalendarIcon} />
                                <InfoRow label="Adresse" value={prospect.adresse_depart.adresse} icon={MapPin} />
                                <InfoRow label="Étage" value={prospect.adresse_depart.etage} />
                                <InfoRow label="Ascenseur" value={getAscenseurLabel(prospect.adresse_depart.ascenseur)} />
                                <InfoRow label="Portage" value={prospect.adresse_depart.portage} />
                                <InfoRow label="Autorisation stationnement" value={prospect.adresse_depart.autorisation_stationnement ? 'Oui' : 'Non'} />
                                <InfoRow label="Monte meuble" value={prospect.adresse_depart.monte_meuble ? 'Oui' : 'Non'} />
                            </>
                        ) : (
                            <div className="p-6 text-center text-gray-500">Aucune adresse de départ renseignée</div>
                        )}
                    </InfoCard>
                    <InfoCard title="Adresse d'arrivée" icon={MapPin} completed={!!(prospect.adresse_arrivee?.adresse)}>
                        {prospect.adresse_arrivee ? (
                            <>
                                <InfoRow label="Date déchargement" value={formatDate(prospect.adresse_arrivee.date)} icon={CalendarIcon} />
                                <InfoRow label="Adresse" value={prospect.adresse_arrivee.adresse} icon={MapPin} />
                                <InfoRow label="Étage" value={prospect.adresse_arrivee.etage} />
                                <InfoRow label="Ascenseur" value={getAscenseurLabel(prospect.adresse_arrivee.ascenseur)} />
                                <InfoRow label="Portage" value={prospect.adresse_arrivee.portage} />
                                <InfoRow label="Autorisation stationnement" value={prospect.adresse_arrivee.autorisation_stationnement ? 'Oui' : 'Non'} />
                                <InfoRow label="Monte meuble" value={prospect.adresse_arrivee.monte_meuble ? 'Oui' : 'Non'} />
                            </>
                        ) : (
                            <div className="p-6 text-center text-gray-500">Aucune adresse d'arrivée renseignée</div>
                        )}
                    </InfoCard>
                    <InfoCard title="Remarques" icon={FileText} completed={!!prospect.remarques}>
                        <div className="p-6">
                            {prospect.remarques ? (
                                <p className="text-[#1B365D] whitespace-pre-wrap">{prospect.remarques}</p>
                            ) : (
                                <p className="text-gray-500 text-center">Aucune remarque</p>
                            )}
                        </div>
                    </InfoCard>

                    {prospect.last_visit_cancel_reason && (
                        <div
                            className="bg-white rounded-xl shadow-sm border border-red-200 border-l-4 border-l-red-500 overflow-hidden"
                            data-testid="prospect-last-cancel-reason"
                        >
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                                <FileText className="w-4 h-4 text-red-500" />
                                <h3 className="text-base font-bold text-gray-900">Dernière visite annulée</h3>
                            </div>
                            <div className="p-5 space-y-2">
                                <p className="text-sm text-red-900 whitespace-pre-wrap break-words">
                                    {prospect.last_visit_cancel_reason}
                                </p>
                                {prospect.last_visit_cancel_date && (
                                    <p className="text-[11px] text-red-700/70">
                                        Annulée le {new Date(prospect.last_visit_cancel_date).toLocaleDateString('fr-FR')} à {new Date(prospect.last_visit_cancel_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                        {prospect.last_visit_cancel_by ? ` par ${prospect.last_visit_cancel_by}` : ''}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Extended sections - only from Agenda/Inventaire */}
                    {isExtended && (
                        <>
                        {/* Formule - Green border */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-emerald-400 overflow-hidden" data-testid="section-formule">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                                <Package className="w-4 h-4 text-emerald-500" />
                                <h3 className="text-base font-bold text-gray-900">Formule</h3>
                            </div>
                            <div className="p-5">
                                {prospect.formules?.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {prospect.formules.map(f => (
                                            <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1B365D]/5 text-[#1B365D] text-sm font-medium border border-[#1B365D]/10">
                                                <Check className="w-3.5 h-3.5 text-emerald-500" />
                                                {FORMULE_LABELS[f] || f}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 text-center">Aucune formule selectionnee</p>
                                )}
                            </div>
                        </div>

                        {/* Garde meuble - Border becomes green when complete */}
                        {(() => {
                            const gm = prospect.garde_meuble;
                            const gmComplete = gm === 'non' || (gm === 'oui' && prospect.garde_meuble_duree && prospect.garde_meuble_mensualite);
                            return (
                        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 ${gmComplete ? 'border-l-emerald-400' : 'border-l-red-400'} overflow-hidden`} data-testid="section-garde-meuble">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                                <Package className={`w-4 h-4 ${gmComplete ? 'text-emerald-500' : 'text-red-400'}`} />
                                <h3 className="text-base font-bold text-gray-900">Garde meuble</h3>
                                {gmComplete && <Check className="w-4 h-4 text-emerald-500 ml-auto" />}
                            </div>
                            <div className="divide-y divide-gray-100">
                                <div className="flex items-center justify-between px-5 py-3">
                                    <span className="text-sm text-gray-500 flex items-center gap-2"><Package className="w-4 h-4" /> Garde meuble</span>
                                    <span className="text-sm font-medium text-gray-900">{GARDE_MEUBLE_LABELS[prospect.garde_meuble] || '-'}</span>
                                </div>
                                {prospect.garde_meuble === 'oui' && (
                                    <>
                                        <div className="flex items-center justify-between px-5 py-3">
                                            <span className="text-sm text-gray-500 flex items-center gap-2"><Clock className="w-4 h-4" /> Duree</span>
                                            <span className="text-sm font-medium text-gray-900">{prospect.garde_meuble_duree || '-'}</span>
                                        </div>
                                        <div className="flex items-center justify-between px-5 py-3">
                                            <span className="text-sm text-gray-500 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Mensualite</span>
                                            <span className="text-sm font-medium text-gray-900">{prospect.garde_meuble_mensualite ? `${prospect.garde_meuble_mensualite} EUR` : '-'}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                            );
                        })()}

                        {/* Calcul tarif - Border becomes green when complete (light statuses only need jours+effectifs) */}
                        {(() => {
                            const sim = prospect.simulateur_devis || {};
                            const cc = sim.calcul_couts || {};
                            const formules = prospect.formules || [];
                            const lightStatuts = ['devis_estimatif', 'listing_telephone', 'listing_email'];
                            const isLight = lightStatuts.includes(prospect.statut_prospect);
                            const has = (v) => v !== undefined && v !== null && String(v).trim() !== '';
                            let calculComplete = formules.length > 0;
                            if (calculComplete) {
                                if (!isLight) {
                                    if (!has(sim.volume_devis) || !has(sim.distance) || !has(sim.camion_20m3) || !has(sim.camion_30m3)) calculComplete = false;
                                }
                                if (calculComplete) {
                                    formules.forEach(f => {
                                        const nbJ = parseInt(cc.nombre_jours?.[f]) || 0;
                                        if (nbJ <= 0) calculComplete = false;
                                        if (!isLight) {
                                            ['tarif_demenageur', 'panier', 'hotel', 'marge'].forEach(k => {
                                                if (!has(cc[k]?.[f])) calculComplete = false;
                                            });
                                        }
                                        for (let i = 1; i <= nbJ; i++) {
                                            if (!has(cc[`effectifs_jr_${i}`]?.[f])) calculComplete = false;
                                        }
                                    });
                                }
                            }
                            return (
                        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 ${calculComplete ? 'border-l-emerald-400' : 'border-l-red-400'} overflow-hidden`} data-testid="section-calcul-tarif">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                                <DollarSign className={`w-4 h-4 ${calculComplete ? 'text-emerald-500' : 'text-red-400'}`} />
                                <h3 className="text-base font-bold text-gray-900">Calcul tarif</h3>
                                {calculComplete && <Check className="w-4 h-4 text-emerald-500 ml-auto" />}
                            </div>
                            <div>
                                {(() => {
                                    const depDate = isoToLocalDate(prospect.adresse_depart?.date);
                                    const arrDate = isoToLocalDate(prospect.adresse_arrivee?.date);
                                    return (
                                    <>
                                {/* Chargement */}
                                <div className="bg-blue-50 px-5 py-2.5 flex items-center justify-between border-b border-blue-100">
                                    <span className="text-sm font-semibold text-blue-600">Date chargement</span>
                                    <span className="text-sm text-gray-700">{depDate || '-'}</span>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    <div className="flex items-center justify-between px-5 py-3">
                                        <span className="text-sm text-gray-500">Nombre de camion reserve</span>
                                        <span className="text-sm font-medium text-gray-900" data-testid="detail-chargement-camions-agenda">
                                            {depDate ? (agendaSummary[depDate]?.camions ?? 0) : '-'}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between px-5 py-3">
                                        <span className="text-sm text-gray-500">Effectif reserve</span>
                                        <span className="text-sm font-medium text-gray-900" data-testid="detail-chargement-effectif-agenda">
                                            {depDate ? (agendaSummary[depDate]?.effectif ?? 0) : '-'}
                                        </span>
                                    </div>
                                </div>

                                {/* Dechargement */}
                                <div className="bg-blue-50 px-5 py-2.5 flex items-center justify-between border-b border-blue-100 border-t border-t-gray-100">
                                    <span className="text-sm font-semibold text-blue-600">Date dechargement</span>
                                    <span className="text-sm text-gray-700">{arrDate || '-'}</span>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    <div className="flex items-center justify-between px-5 py-3">
                                        <span className="text-sm text-gray-500">Nombre de camion reserve</span>
                                        <span className="text-sm font-medium text-gray-900" data-testid="detail-dechargement-camions-agenda">
                                            {arrDate ? (agendaSummary[arrDate]?.camions ?? 0) : '-'}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between px-5 py-3">
                                        <span className="text-sm text-gray-500">Effectif reserve</span>
                                        <span className="text-sm font-medium text-gray-900" data-testid="detail-dechargement-effectif-agenda">
                                            {arrDate ? (agendaSummary[arrDate]?.effectif ?? 0) : '-'}
                                        </span>
                                    </div>
                                </div>
                                    </>
                                    );
                                })()}

                                {/* End dechargement */}

                                {/* Prepar/Stock entries */}
                                {(prospect.prepar_stock || []).map((ps, idx) => (
                                    <div key={idx}>
                                        <div className="bg-amber-50 px-5 py-2.5 flex items-center justify-between border-b border-amber-100 border-t border-t-gray-100">
                                            <span className="text-sm font-semibold text-amber-700">Prepar/Stock {idx + 1}</span>
                                            <span className="text-sm text-gray-700">{ps.date || '-'}</span>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            <div className="flex items-center justify-between px-5 py-3">
                                                <span className="text-sm text-gray-500">Nombre de camion reserve</span>
                                                <span className="text-sm font-medium text-gray-900" data-testid={`detail-prepar-camions-agenda-${idx}`}>
                                                    {ps.date ? (agendaSummary[ps.date]?.camions ?? 0) : '-'}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between px-5 py-3">
                                                <span className="text-sm text-gray-500">Effectif reserve</span>
                                                <span className="text-sm font-medium text-gray-900" data-testid={`detail-prepar-effectif-agenda-${idx}`}>
                                                    {ps.date ? (agendaSummary[ps.date]?.effectif ?? 0) : '-'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* ===== SIMULATEUR DE DEVIS (read-only) ===== */}
                                {prospect.simulateur_devis && (() => {
                                    const activeCols = FORMULE_COLUMNS.filter(c => (prospect.formules || []).includes(c.key));
                                    // Compute totals per formule (same rules as ProspectForm)
                                    const sim = prospect.simulateur_devis || {};
                                    const cc = sim.calcul_couts || {};
                                    const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
                                    const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
                                    const baseFilled = ['volume_devis', 'camion_20m3', 'camion_30m3', 'distance'].every(k => isFilled(sim[k]));
                                    const fournAct = sim.fournitures_active === true;
                                    const fournFilled = !fournAct || ['cartons_standards', 'cartons_livres', 'adhesif', 'papier_bulle', 'autres'].every(k => isFilled(sim[k]));
                                    const fournCost = fournAct ? num(sim.cartons_standards) + num(sim.cartons_livres) + num(sim.adhesif) + num(sim.papier_bulle) * 10 + num(sim.autres) : 0;
                                    const nbCamions = num(sim.camion_20m3) + num(sim.camion_30m3);
                                    const distanceCost = num(sim.distance) * nbCamions * 0.9;
                                    const sharedRows = [
                                        { key: 'prix_aot_depart', show: prospect.adresse_depart?.autorisation_stationnement === true },
                                        { key: 'prix_aot_arrivee', show: prospect.adresse_arrivee?.autorisation_stationnement === true },
                                        { key: 'prix_monte_meuble_depart', show: prospect.adresse_depart?.monte_meuble === true },
                                        { key: 'prix_monte_meuble_arrivee', show: prospect.adresse_arrivee?.monte_meuble === true },
                                    ];
                                    let sharedCost = 0, sharedFilled = true;
                                    sharedRows.forEach(r => { if (!r.show) return; const v = cc[r.key]?.value; if (!isFilled(v)) sharedFilled = false; sharedCost += num(v); });
                                    const totals = {};
                                    activeCols.forEach(({ key: formule }) => {
                                        const nbJours = parseInt(cc.nombre_jours?.[formule]) || 0;
                                        let perFilled = isFilled(cc.nombre_jours?.[formule]);
                                        ['tarif_demenageur', 'panier', 'hotel', 'marge'].forEach(k => { if (!isFilled(cc[k]?.[formule])) perFilled = false; });
                                        for (let i = 1; i <= nbJours; i++) { if (!isFilled(cc[`effectifs_jr_${i}`]?.[formule])) perFilled = false; }
                                        const complete = baseFilled && fournFilled && sharedFilled && perFilled && nbJours > 0;
                                        const perCost = num(cc.tarif_demenageur?.[formule]) + num(cc.panier?.[formule]) + num(cc.hotel?.[formule]) + num(cc.marge?.[formule]);
                                        const ht = fournCost + distanceCost + perCost + sharedCost;
                                        totals[formule] = { ht, ttc: ht * 1.2, complete };
                                    });
                                    return (
                                    <div className="border-t-2 border-gray-200 mt-2">
                                        {/* Prix cards - only selected formules */}
                                        {activeCols.length > 0 && (
                                        <div className={`grid gap-3 px-5 pt-5 pb-4 ${activeCols.length === 1 ? 'grid-cols-1' : activeCols.length === 2 ? 'grid-cols-2' : activeCols.length === 3 ? 'grid-cols-3' : 'grid-cols-2 lg:grid-cols-4'}`}>
                                            {activeCols.map(({ key, label, border }) => {
                                                const t = totals[key];
                                                return (
                                                <div key={key} className={`bg-blue-50/60 rounded-lg border border-blue-200/60 border-l-4 ${border} p-4 text-center`}>
                                                    <span className="text-xs font-bold text-blue-600 tracking-wider">{label}</span>
                                                    <p className="text-xl font-bold text-[#1B365D] mt-1">
                                                        {t?.complete ? `${t.ttc.toFixed(2).replace('.', ',')} €` : '— €'}
                                                    </p>
                                                    <span className="text-xs text-gray-400">TTC</span>
                                                </div>
                                                );
                                            })}
                                        </div>
                                        )}

                                        {/* Informations de base */}
                                        <div className="px-5 pb-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Calculator className="w-4 h-4 text-[#1B365D]/60" />
                                                <h4 className="text-sm font-bold text-[#1B365D] uppercase tracking-wide">Informations de base</h4>
                                            </div>
                                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                                {[
                                                    { key: 'volume_devis', label: 'Volume devis' },
                                                    { key: 'camion_20m3', label: 'Camion 20 m\u00b3' },
                                                    { key: 'camion_30m3', label: 'Camion 30 m\u00b3' },
                                                    { key: 'distance', label: 'Distance (km)' },
                                                ].map(({ key, label }) => (
                                                    <div key={key} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                                        <span className="text-xs text-gray-500">{label}</span>
                                                        <p className="text-sm font-semibold text-[#1B365D] mt-0.5">{prospect.simulateur_devis[key] || '0'}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Fournitures */}
                                        <div className="px-5 pb-5">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <Gift className="w-4 h-4 text-[#1B365D]/60" />
                                                    <h4 className="text-sm font-bold text-[#1B365D] uppercase tracking-wide">Fournitures</h4>
                                                </div>
                                                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                                                    prospect.simulateur_devis.fournitures_active
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-gray-100 text-gray-500'
                                                }`}>{prospect.simulateur_devis.fournitures_active ? 'Oui' : 'Non'}</span>
                                            </div>
                                            {prospect.simulateur_devis.fournitures_active && (
                                                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                                                    {[
                                                        { key: 'cartons_standards', label: 'Cartons standards' },
                                                        { key: 'cartons_livres', label: 'Cartons livres' },
                                                        { key: 'adhesif', label: 'Adhesif' },
                                                        { key: 'papier_bulle', label: 'Papier bulle' },
                                                        { key: 'autres', label: 'Autres' },
                                                    ].map(({ key, label }) => (
                                                        <div key={key} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                                            <span className="text-xs text-gray-500">{label}</span>
                                                            <p className="text-sm font-semibold text-[#1B365D] mt-0.5">{prospect.simulateur_devis[key] || '0'}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Calcul des couts table (read-only) */}
                                        {activeCols.length > 0 && (
                                        <div className="px-5 pb-5">
                                            <div className="flex items-center gap-2 mb-4">
                                                <DollarSign className="w-4 h-4 text-[#1B365D]/60" />
                                                <h4 className="text-sm font-bold text-[#1B365D] uppercase tracking-wide">Calcul des couts</h4>
                                            </div>
                                            <div className="bg-gray-50/50 rounded-xl border border-gray-200 overflow-hidden">
                                                <div className="grid bg-[#1B365D]/5 border-b border-gray-200" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                    <div className="px-4 py-3"><span className="text-xs font-bold text-[#1B365D] uppercase tracking-wider">Ligne</span></div>
                                                    {activeCols.map(({ key, label, color }) => (
                                                        <div key={key} className="px-3 py-3 text-center"><span className={`text-xs font-bold ${color} uppercase tracking-wider`}>{label}</span></div>
                                                    ))}
                                                </div>
                                                {[
                                                    { key: 'nombre_jours', label: 'Nombre de jours' },
                                                    { key: 'tarif_demenageur', label: 'Tarif demenageur (\u20ac)' },
                                                    { key: 'panier', label: 'Panier (\u20ac)' },
                                                    { key: 'hotel', label: 'Hotel (\u20ac)' },
                                                ].map(({ key, label }) => (
                                                    <div key={key} className="grid border-b border-gray-100" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                        <div className="px-4 py-3 flex items-center">
                                                            <span className="text-sm text-gray-700">{label}</span>
                                                        </div>
                                                        {activeCols.map(({ key: formule }) => (
                                                            <div key={formule} className="px-3 py-3 text-center">
                                                                <span className="text-sm font-medium text-gray-900">
                                                                    {prospect.simulateur_devis?.calcul_couts?.[key]?.[formule] || '-'}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                                {/* Single-field conditional rows */}
                                                {[
                                                    { key: 'prix_aot_depart', label: 'Prix AOT depart', show: prospect.adresse_depart?.autorisation_stationnement === true },
                                                    { key: 'prix_aot_arrivee', label: 'Prix AOT arrivee', show: prospect.adresse_arrivee?.autorisation_stationnement === true },
                                                    { key: 'prix_monte_meuble_depart', label: 'Prix Monte-meuble depart', show: prospect.adresse_depart?.monte_meuble === true },
                                                    { key: 'prix_monte_meuble_arrivee', label: 'Prix Monte-meuble arrivee', show: prospect.adresse_arrivee?.monte_meuble === true },
                                                ].filter(r => r.show).map(({ key, label }) => (
                                                    <div key={key} className="grid border-b border-gray-100 bg-blue-50/40" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                        <div className="px-4 py-3 flex items-center">
                                                            <span className="text-sm text-blue-600 font-medium italic">{label}</span>
                                                        </div>
                                                        <div className="px-3 py-3 flex items-center justify-center" style={{ gridColumn: `2 / ${activeCols.length + 2}` }}>
                                                            <span className="text-sm font-medium text-gray-900">{prospect.simulateur_devis?.calcul_couts?.[key]?.value || '-'}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                                {/* Marge row */}
                                                <div className="grid border-b border-gray-100" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                    <div className="px-4 py-3 flex items-center">
                                                        <span className="text-sm text-gray-700">Marge (\u20ac)</span>
                                                    </div>
                                                    {activeCols.map(({ key: formule }) => (
                                                        <div key={formule} className="px-3 py-3 text-center">
                                                            <span className="text-sm font-medium text-gray-900">
                                                                {prospect.simulateur_devis?.calcul_couts?.marge?.[formule] || '-'}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="grid border-b border-gray-200 bg-gray-50" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                    <div className="px-4 py-3"><span className="text-sm font-bold text-[#1B365D]">Total HT</span></div>
                                                    {activeCols.map(({ key }) => {
                                                        const t = totals[key];
                                                        return (
                                                            <div key={key} className="px-3 py-3 text-center"><span className="text-sm font-bold text-[#1B365D]">{t?.complete ? `${t.ht.toFixed(2).replace('.', ',')} €` : '— €'}</span></div>
                                                        );
                                                    })}
                                                </div>
                                                <div className="grid bg-[#1B365D]/5" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                    <div className="px-4 py-3"><span className="text-sm font-bold text-blue-600">Total TTC</span></div>
                                                    {activeCols.map(({ key }) => {
                                                        const t = totals[key];
                                                        return (
                                                            <div key={key} className="px-3 py-3 text-center"><span className="text-sm font-bold text-blue-600">{t?.complete ? `${t.ttc.toFixed(2).replace('.', ',')} €` : '— €'}</span></div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                        )}
                                    </div>
                                    );
                                })()}
                            </div>
                        </div>
                            );
                        })()}

                        {/* Volume Section */}
                        <VolumeSection prospectId={id} />
                        </>
                    )}
                </div>
            </div>
            {/* ========= VISIT SCHEDULING MODAL ========= */}
            <Dialog open={showVisitModal} onOpenChange={(open) => {
                if (!open) { confirmNavigation(() => { setShowVisitModal(false); setReprogramVisitId(null); setHasChanges(false); }); }
            }}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="visit-modal">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <CalendarIcon className="w-5 h-5 text-[#C9A962]" />
                            {reprogramVisitId ? 'Reprogrammer la visite' : 'Planifier la visite'}
                        </DialogTitle>
                        <DialogDescription>
                            {reprogramVisitId
                                ? `Une visite est déjà prévue pour ${prospect?.nom}. Modifiez la date / l'heure pour la reprogrammer.`
                                : `${visitType === 'visite_presentiel' ? 'Visite présentiel' : 'Visite virtuelle'} pour ${prospect?.nom}`}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateVisit}>
                        <div className="space-y-4 py-4">
                            {/* Visit info */}
                            <div className="space-y-2">
                                <Label>Type de visite</Label>
                                <div className="flex gap-2">
                                    <Badge className={`${visitType === 'visite_presentiel' ? 'bg-[#1B365D]' : 'bg-[#C9A962]'} text-white px-3 py-1`}>
                                        {visitType === 'visite_presentiel' ? 'Visite présentiel' : 'Visite virtuelle'}
                                    </Badge>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="visit-date">Date *</Label>
                                <Input
                                    id="visit-date"
                                    type="date"
                                    min={todayISO()}
                                    value={visitForm.date}
                                    onChange={(e) => setVisitForm({ ...visitForm, date: e.target.value })}
                                    data-testid="visit-date-input"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Heure début</Label>
                                    <Select
                                        value={visitForm.start_time}
                                        onValueChange={(v) => setVisitForm(prev => ({
                                            ...prev,
                                            start_time: v,
                                            // Default to a 1h slot: end_time = start_time + 1h. User can still
                                            // override end_time afterwards via the next Select.
                                            end_time: addMinutes(v, 60),
                                        }))}
                                    >
                                        <SelectTrigger data-testid="visit-start-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Heure fin</Label>
                                    <Select value={visitForm.end_time} onValueChange={(v) => setVisitForm({ ...visitForm, end_time: v })}>
                                        <SelectTrigger data-testid="visit-end-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {canAssign && (
                                <div className="space-y-2">
                                    <Label>Assigner à un commercial</Label>
                                    <Select value={visitForm.assigned_to || '_none'} onValueChange={(v) => setVisitForm({ ...visitForm, assigned_to: v === '_none' ? '' : v })}>
                                        <SelectTrigger data-testid="visit-assign-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="_none">— Non assigné —</SelectItem>
                                            {users.map(u => (
                                                <SelectItem key={u.id} value={u.id}>
                                                    {u.name} <span className="text-xs text-gray-400 capitalize">({u.role})</span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* Prospect info summary */}
                            <div className="border-t pt-4 mt-4">
                                <h4 className="font-semibold text-[#1B365D] mb-3 flex items-center gap-2">
                                    <User className="w-4 h-4 text-[#C9A962]" />
                                    Informations du prospect
                                </h4>
                                <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                        <User className="w-3.5 h-3.5 text-gray-400" />
                                        <span className="text-gray-500">Nom :</span>
                                        <span className="font-medium text-[#1B365D]">{prospect?.nom}</span>
                                    </div>
                                    {prospect?.telephone && (
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-500">Tél :</span>
                                            <span className="font-medium text-[#1B365D]">{prospect.telephone}</span>
                                        </div>
                                    )}
                                    {prospect?.email && (
                                        <div className="flex items-center gap-2">
                                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-500">Email :</span>
                                            <span className="font-medium text-[#1B365D]">{prospect.email}</span>
                                        </div>
                                    )}
                                    {prospect?.adresse_depart?.adresse && (
                                        <div className="flex items-center gap-2">
                                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-500">Adresse :</span>
                                            <span className="font-medium text-[#1B365D]">{prospect.adresse_depart.adresse}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => confirmNavigation(() => { setShowVisitModal(false); setReprogramVisitId(null); setHasChanges(false); })}>Annuler</Button>
                            <Button type="submit" disabled={creatingVisit} className="bg-[#1B365D] hover:bg-[#2A4A7A]" data-testid="visit-submit-button">
                                {creatingVisit
                                    ? (reprogramVisitId ? 'Reprogrammation...' : 'Planification...')
                                    : (reprogramVisitId ? 'Reprogrammer la visite' : 'Planifier la visite')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <UnsavedDialog />

            {/* ========= EMAIL CONFIRMATION DIALOG ========= */}
            <Dialog open={!!emailConfirm} onOpenChange={(open) => { if (!open) setEmailConfirm(null); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <Mail className="w-5 h-5 text-[#C9A962]" />
                            Envoyer un email de confirmation ?
                        </DialogTitle>
                        <DialogDescription>
                            Souhaitez-vous envoyer un email de confirmation de rendez-vous à <strong>{emailConfirm?.clientName}</strong> ?
                        </DialogDescription>
                    </DialogHeader>
                    {emailConfirm && (
                        <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm border" data-testid="email-confirm-details">
                            <div className="flex items-center gap-2 text-gray-600">
                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                                <span className="text-gray-500">Email :</span>
                                <span className="font-medium text-[#1B365D]">{emailConfirm.clientEmail}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                                <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                                <span className="text-gray-500">Date :</span>
                                <span className="font-medium text-[#1B365D]">{emailConfirm.date} à {emailConfirm.heure}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                <span className="text-gray-500">Type :</span>
                                <span className="font-medium text-[#1B365D]">
                                    {emailConfirm.visitType === 'visite_virtuelle' ? 'Visite virtuelle' : 'Visite présentiel'}
                                </span>
                            </div>
                            {emailConfirm.address && (
                                <div className="flex items-center gap-2 text-gray-600">
                                    <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                    <span className="text-gray-500">Adresse :</span>
                                    <span className="font-medium text-[#1B365D]">{emailConfirm.address}</span>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={() => setEmailConfirm(null)} data-testid="skip-email-btn">
                            Ne pas envoyer
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSendConfirmationEmail}
                            disabled={sendingEmail}
                            className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-1.5"
                            data-testid="send-confirmation-btn"
                        >
                            <Send className="w-4 h-4" />
                            {sendingEmail ? 'Envoi en cours...' : 'Envoyer la confirmation'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialogue: Remarque requise pour passage en statut sans-réponse */}
            <Dialog open={remarkDialogOpen} onOpenChange={(v) => { if (!savingRemark) setRemarkDialogOpen(v); }}>
                <DialogContent className="sm:max-w-md" data-testid="remark-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D]">
                            {STATUTS.find(s => s.value === pendingStatus)?.label || 'Remarque requise'}
                        </DialogTitle>
                        <DialogDescription>
                            Pour passer à ce statut, veuillez indiquer combien d'appels vous avez effectués et toute précision utile.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-3 space-y-2">
                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Remarque</label>
                        <Textarea
                            value={remarkText}
                            onChange={(e) => setRemarkText(e.target.value)}
                            placeholder="Ex : 3 appels sans réponse - injoignable, pas de message vocal disponible..."
                            rows={4}
                            className="resize-none text-sm"
                            data-testid="remark-input"
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRemarkDialogOpen(false)} disabled={savingRemark} data-testid="remark-cancel">
                            Annuler
                        </Button>
                        <Button
                            className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white"
                            onClick={handleRemarkConfirm}
                            disabled={savingRemark || !remarkText.trim()}
                            data-testid="remark-confirm"
                        >
                            {savingRemark ? 'Enregistrement…' : 'Valider'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rappel dialog (a_rappeler / listing_attente) */}
            <Dialog open={rappelDialogOpen} onOpenChange={(v) => { if (!savingRappel) setRappelDialogOpen(v); }}>
                <DialogContent className="sm:max-w-md" data-testid="rappel-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D]">
                            {STATUTS.find(s => s.value === pendingRappelStatus)?.label || 'Programmer un rappel'}
                        </DialogTitle>
                        <DialogDescription>
                            Indiquez quand rappeler le prospect et un commentaire utile pour préparer l'échange.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-3 space-y-4">
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Date de rappel</label>
                            <input
                                type="date"
                                value={rappelDate}
                                onChange={(e) => setRappelDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-[#1B365D]"
                                data-testid="rappel-date-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Remarque</label>
                            <Textarea
                                value={rappelRemark}
                                onChange={(e) => setRappelRemark(e.target.value)}
                                placeholder="Ex : Le client veut réfléchir, rappeler avec une nouvelle proposition de date..."
                                rows={4}
                                className="resize-none text-sm"
                                data-testid="rappel-remark-input"
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRappelDialogOpen(false)} disabled={savingRappel} data-testid="rappel-cancel">
                            Annuler
                        </Button>
                        <Button
                            className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white"
                            onClick={handleRappelConfirm}
                            disabled={savingRappel || !rappelRemark.trim() || !rappelDate}
                            data-testid="rappel-confirm"
                        >
                            {savingRappel ? 'Enregistrement…' : 'Programmer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
